import { create } from 'zustand';
import { http } from '@/lib/http';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parsePreferenceValue = (raw) => {
  if (raw === null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
};

// ─── Store ───────────────────────────────────────────────────────────────────

const store = create(() => ({
  preferences: {},
  isLoading: false,
  isLoaded: false,
}));

// ─── React hooks ─────────────────────────────────────────────────────────────

export const usePreferences = () => store((state) => state.preferences);
export const useIsPreferencesLoading = () => store((state) => state.isLoading);
export const useIsPreferencesLoaded = () => store((state) => state.isLoaded);

// ─── Server API methods  ───────────────────────────────────────────

const fetchAllFromServer = async () => {
  const res = await http.get('/user_preference');
  const raw = res?.data?.preferences ?? {};
  return Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [
      key,
      parsePreferenceValue(value),
    ])
  );
};

const saveItemInServer = (key, value) => {
  return http
    .patch('/user_preference', {
      user_preference: { [key]: value !== null ? JSON.stringify(value) : null },
    })
    .catch((err) => console.error('Failed to save preference', key, err));
};

const removeItemInServer = (key) => {
  return http
    .patch('/user_preference', {
      user_preference: { [key]: null },
    })
    .catch((err) => console.error('Failed to remove preference', key, err));
};

// Consumers only call these. Server sync is handled internally.

export const fetchAllUserPreferences = async () => {
  store.setState({ isLoading: true });
  try {
    const parsed = await fetchAllFromServer();
    store.setState({ preferences: parsed, isLoaded: true });
  } catch (error) {
    console.error('Error fetching user preferences', error);
    store.setState({ isLoaded: true });
  } finally {
    store.setState({ isLoading: false });
  }
};

export const getItem = (key) => {
  return store.getState().preferences[key] ?? null;
};

export const getPreferences = () => store.getState().preferences;

export const setItem = (key, value) => {
  store.setState((state) => ({
    preferences: { ...state.preferences, [key]: value },
  }));
  saveItemInServer(key, value);
};

export const updateItem = (key, value) => {
  store.setState((state) => ({
    preferences: { ...state.preferences, [key]: value },
  }));
  saveItemInServer(key, value);
};

export const removeItem = (key) => {
  store.setState((state) => ({
    preferences: { ...state.preferences, [key]: null },
  }));
  removeItemInServer(key);
};
